import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Truck, RefreshCw, Bell, Send, Check, Clock, MapPin, Phone,
  ChevronDown, ChevronUp, Calendar, CalendarDays, CalendarRange,
  AlertTriangle, Package, RotateCcw, Navigation, List, Map,
  ChevronLeft, ChevronRight, User, FileText, X, Edit3, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { formatDate, formatDateShort, formatDateWithDay } from '../utils/dateUtils';

// Import Leaflet dynamiquement pour éviter SSR issues
let LeafletMap = null;
let LeafletTileLayer = null;
let LeafletMarker = null;
let LeafletPopup = null;
let LeafletPolyline = null;
let L = null;

try {
  const leaflet = require('leaflet');
  const rl = require('react-leaflet');
  L = leaflet;
  LeafletMap = rl.MapContainer;
  LeafletTileLayer = rl.TileLayer;
  LeafletMarker = rl.Marker;
  LeafletPopup = rl.Popup;
  LeafletPolyline = rl.Polyline;
  require('leaflet/dist/leaflet.css');
  // Fix icônes Leaflet avec webpack
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl:       'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl:     'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  });
} catch (e) { /* eslint-disable-line no-empty */ }

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// ─── Constantes ──────────────────────────────────────────────────────────────
const STATUT_COLORS = {
  acompte_paye: 'var(--admin-gold)',
  solde_paye:   'var(--admin-green)',
  en_livraison: 'var(--admin-teal)',
  livre:        'var(--admin-green)',
  repris:       'var(--admin-muted)',
};
const STATUT_LABELS = {
  acompte_paye: 'Acompte payé',
  solde_paye:   'Soldé ✓',
  en_livraison: 'En livraison',
  livre:        'Livré ✓',
  repris:       'Repris ✓',
};

const CRENEAUX_LIVRAISON = [
  '08:00','09:00','10:00','11:00','12:00','14:00','15:00','16:00','17:00','18:00'
];
const CRENEAUX_RETOUR = [
  '07:00','08:00','09:00','09:30','10:00','11:00','12:00'
];

const MONTH_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const DAY_SHORT = ['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'];

// ─── Géocodage via API Adresse gouv ──────────────────────────────────────────
async function geocodeAddress(address) {
  try {
    const res = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(address)}&limit=1`);
    const data = await res.json();
    const feat = data.features?.[0];
    if (feat) {
      const [lon, lat] = feat.geometry.coordinates;
      return { lat, lon, label: feat.properties.label };
    }
  } catch { /* eslint-disable-line no-empty */ }
  return null;
}

// ─── Icône colorée Leaflet ────────────────────────────────────────────────────
function makeColorIcon(color) {
  if (!L) return null;
  return L.divIcon({
    className: '',
    html: `<div style="width:28px;height:28px;border-radius:50% 50% 50% 0;background:${color};border:2px solid #fff;transform:rotate(-45deg);box-shadow:0 2px 8px rgba(0,0,0,0.4)"></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    popupAnchor: [0, -28],
  });
}

// ─── Composant Carte ─────────────────────────────────────────────────────────
function CartelivraIsons({ livraisons }) {
  const [geocoded, setGeocoded] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      setLoading(true);
      const results = [];
      for (const liv of livraisons) {
        const addr = liv.adresse_livraison || liv.session?.adresse || '';
        if (!addr) continue;
        const geo = await geocodeAddress(addr);
        if (geo && !cancelled) {
          results.push({ ...liv, lat: geo.lat, lon: geo.lon, adresse_geocodee: geo.label });
        }
      }
      if (!cancelled) {
        setGeocoded(results);
        setLoading(false);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [livraisons]);

  if (!LeafletMap) return (
    <div className="flex items-center justify-center rounded-2xl" style={{ height: 360, background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Planning</p>
        <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
          letterSpacing: '-.6px', color: 'var(--admin-text)', marginTop: 4 }}>Calendrier livraisons</h2>
        <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>Planning, carte et creneaux</p>
      </div>

      <p style={{ color: 'var(--admin-faint)' }}>Carte non disponible</p>
    </div>
  );

  // Centre par défaut : Figeac
  const center = geocoded.length > 0 ? [geocoded[0].lat, geocoded[0].lon] : [44.6049, 2.0319];
  // Itinéraire (polyline reliant les points dans l'ordre)
  const route = geocoded.map(g => [g.lat, g.lon]);

  return (
    <div className="rounded-2xl overflow-hidden" style={{ height: 380, border: '1px solid var(--admin-border-soft)' }}>
      {loading ? (
        <div className="flex items-center justify-center h-full" style={{ background: 'var(--admin-surface)' }}>
          <div className="text-center">
            <div className="animate-spin w-8 h-8 rounded-full mx-auto mb-2" style={{ border: '2px solid rgba(255,255,255,0.1)', borderTopColor: 'var(--admin-teal)' }} />
            <p className="text-xs" style={{ color: 'var(--admin-faint)' }}>Géolocalisation en cours…</p>
          </div>
        </div>
      ) : (
        <LeafletMap center={center} zoom={geocoded.length > 1 ? 10 : 13} style={{ height: '100%', width: '100%' }}>
          <LeafletTileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='© <a href="https://openstreetmap.org">OpenStreetMap</a>'
          />
          {/* Itinéraire suggéré */}
          {route.length > 1 && (
            <LeafletPolyline positions={route} pathOptions={{ color: 'var(--admin-teal)', weight: 3, dashArray: '8 6', opacity: 0.7 }} />
          )}
          {/* Marqueurs */}
          {geocoded.map((liv, idx) => {
            const color = STATUT_COLORS[liv.statut] || 'var(--admin-teal)';
            const icon = makeColorIcon(color);
            return (
              <LeafletMarker key={liv.id || idx} position={[liv.lat, liv.lon]} icon={icon}>
                <LeafletPopup>
                  <div style={{ minWidth: 200 }}>
                    <p style={{ fontWeight: 700, marginBottom: 4 }}>
                      <span style={{ background: color, color: 'var(--admin-text)', borderRadius: 4, padding: '2px 6px', fontSize: 11 }}>#{idx + 1}</span>
                      {' '}{liv.client_nom}
                    </p>
                    <p style={{ fontSize: 12, color: '#555', margin: '4px 0' }}>📅 {liv.date_evenement}</p>
                    <p style={{ fontSize: 12, color: '#555', margin: '4px 0' }}>🕐 Livraison : {liv.heure_livraison || 'À définir'}</p>
                    <p style={{ fontSize: 12, color: '#555', margin: '4px 0' }}>↩️ Retour : {liv.heure_retour || 'lendemain 10h'}</p>
                    <p style={{ fontSize: 12, color: '#777', margin: '4px 0' }}>📍 {liv.adresse_geocodee || liv.adresse_livraison}</p>
                    <p style={{ fontSize: 12, fontWeight: 700, color: color, margin: '4px 0' }}>{liv.numero} · {liv.total}€</p>
                    {liv.client_telephone && (
                      <p style={{ fontSize: 12, color: '#555', margin: '4px 0' }}>📞 {liv.client_telephone}</p>
                    )}
                  </div>
                </LeafletPopup>
              </LeafletMarker>
            );
          })}
        </LeafletMap>
      )}
    </div>
  );
}

// ─── Composant modal détail livraison ────────────────────────────────────────
function LivraisonDetailModal({ livraison, onClose, onSave }) {
  const [heureLivraison, setHeureLivraison] = useState(livraison.heure_livraison || '');
  const [heureRetour, setHeureRetour] = useState(livraison.heure_retour || '10:00');
  const [notes, setNotes] = useState(livraison.notes_livraison || '');
  const [saving, setSaving] = useState(false);
  const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
  const H = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const s = STATUT_COLORS[livraison.statut] || 'var(--admin-teal)';

  async function save() {
    setSaving(true);
    try {
      await fetch(`${BACKEND}/api/admin/livraisons/${livraison.id}/creneaux`, {
        method: 'PUT', headers: H,
        body: JSON.stringify({ heure_livraison: heureLivraison, heure_retour: heureRetour, notes_livraison: notes }),
      });
      toast.success('Créneaux mis à jour !');
      onSave();
      onClose();
    } catch { toast.error('Erreur'); }
    setSaving(false);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backdropFilter: 'blur(8px)', background: 'rgba(0,0,0,0.75)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-lg rounded-2xl overflow-hidden" style={{ background: 'var(--admin-bg)', border: '1px solid var(--admin-border)' }}>

        {/* Header */}
        <div className="flex items-center justify-between p-5" style={{ borderBottom: '1px solid var(--admin-border-soft)', background: 'rgba(255,255,255,0.02)' }}>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-base" style={{ color: 'var(--admin-text)' }}>{livraison.client_nom}</h3>
              <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                style={{ background: `${s}20`, color: s }}>
                {STATUT_LABELS[livraison.statut] || livraison.statut}
              </span>
            </div>
            <p className="text-xs mt-0.5" style={{ color: 'var(--admin-faint)' }}>
              {livraison.numero} · {livraison.date_evenement}
            </p>
          </div>
          <button onClick={onClose} style={{ color: 'var(--admin-muted)', background: 'none', border: 'none', cursor: 'pointer' }}>
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Infos client */}
          <div className="grid grid-cols-2 gap-3 p-3 rounded-xl" style={{ background: 'var(--admin-surface)' }}>
            <div>
              <p className="text-xs mb-1" style={{ color: 'var(--admin-faint)' }}>Client</p>
              <p className="text-sm font-semibold" style={{ color: 'var(--admin-text)' }}>{livraison.client_nom}</p>
              <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{livraison.client_email}</p>
            </div>
            <div>
              <p className="text-xs mb-1" style={{ color: 'var(--admin-faint)' }}>Téléphone</p>
              <p className="text-sm font-semibold" style={{ color: 'var(--admin-teal)' }}>{livraison.client_telephone || '—'}</p>
              <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{livraison.total}€ TTC</p>
            </div>
          </div>

          {/* Adresse */}
          <div className="flex items-start gap-2 p-3 rounded-xl" style={{ background: 'var(--admin-surface)' }}>
            <MapPin size={14} style={{ color: 'var(--admin-teal)', flexShrink: 0, marginTop: 2 }} />
            <div>
              <p className="text-xs mb-0.5" style={{ color: 'var(--admin-faint)' }}>Adresse de livraison</p>
              <p className="text-sm font-semibold" style={{ color: 'var(--admin-text)' }}>
                {livraison.adresse_livraison || livraison.session?.adresse || 'Non renseignée'}
              </p>
            </div>
          </div>

          {/* Articles */}
          {livraison.items?.length > 0 && (
            <div className="p-3 rounded-xl" style={{ background: 'var(--admin-surface)' }}>
              <p className="text-xs mb-2 font-semibold" style={{ color: 'var(--admin-faint)' }}>MATÉRIEL</p>
              {livraison.items.map((item, i) => (
                <div key={i} className="flex justify-between items-center py-1" style={{ borderBottom: i < livraison.items.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                  <p className="text-sm" style={{ color: 'var(--admin-text)' }}>{item.product_nom} <span style={{ color: 'var(--admin-faint)' }}>×{item.quantite}</span></p>
                  <span className="text-xs" style={{ color: 'var(--admin-gold)' }}>{item.sous_total || item.prix_unitaire * item.quantite}€</span>
                </div>
              ))}
            </div>
          )}

          {/* ── Créneaux horaires ── */}
          <div className="p-4 rounded-xl space-y-4" style={{ background: 'rgba(93,188,191,0.06)', border: '1px solid var(--admin-teal-line)' }}>
            <p className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--admin-teal)' }}>Créneaux horaires</p>

            {/* Livraison */}
            <div>
              <p className="text-xs mb-2 flex items-center gap-1.5" style={{ color: 'var(--admin-muted)' }}>
                <Truck size={11} /> Heure de livraison
              </p>
              <div className="flex flex-wrap gap-2">
                {CRENEAUX_LIVRAISON.map(h => (
                  <button key={h} onClick={() => setHeureLivraison(h)}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                    style={{
                      background: heureLivraison === h ? 'var(--admin-teal)' : 'var(--admin-border-soft)',
                      color: heureLivraison === h ? '#0B1E20' : 'rgba(255,255,255,0.6)',
                      border: `1px solid ${heureLivraison === h ? 'var(--admin-teal)' : 'rgba(255,255,255,0.08)'}`,
                    }}>
                    {h}
                  </button>
                ))}
              </div>
            </div>

            {/* Retour */}
            <div>
              <p className="text-xs mb-2 flex items-center gap-1.5" style={{ color: 'var(--admin-muted)' }}>
                <RotateCcw size={11} /> Heure de retour (lendemain)
              </p>
              <div className="flex flex-wrap gap-2">
                {CRENEAUX_RETOUR.map(h => (
                  <button key={h} onClick={() => setHeureRetour(h)}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                    style={{
                      background: heureRetour === h ? 'var(--admin-gold)' : 'var(--admin-border-soft)',
                      color: heureRetour === h ? '#0B1E20' : 'rgba(255,255,255,0.6)',
                      border: `1px solid ${heureRetour === h ? 'var(--admin-gold)' : 'rgba(255,255,255,0.08)'}`,
                    }}>
                    {h}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <p className="text-xs mb-1.5" style={{ color: 'var(--admin-muted)' }}>Notes livraison</p>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
              placeholder="Instructions particulières, code d'accès, contact sur place…"
              className="w-full px-3 py-2 rounded-xl text-sm resize-none"
              style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', color: 'var(--admin-text)', outline: 'none' }} />
          </div>

          {/* Bouton save */}
          <button onClick={save} disabled={saving}
            className="w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2"
            style={{ background: 'var(--admin-green)', color: 'var(--admin-text)', border: 'none', cursor: 'pointer' }}>
            <Check size={15} /> {saving ? 'Enregistrement…' : 'Enregistrer les créneaux'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────
export default function AdminLivraisons() {
  const [planning, setPlanning] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState({});
  const [runningAuto, setRunningAuto] = useState(false);

  // Vue
  const [viewMode, setViewMode] = useState('semaine'); // jour | semaine | mois
  const [displayMode, setDisplayMode] = useState('liste'); // liste | carte
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedLivraison, setSelectedLivraison] = useState(null);

  const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
  const H = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/admin/livraisons/planning?semaines=12`, { headers: H });
      const data = await res.json();
      setPlanning(data.livraisons || []);
      setStats(data.stats || {});
    } catch { toast.error('Erreur chargement'); }
    setLoading(false);
  // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Navigation calendrier ──────────────────────────────────────────────────
  function navigatePrev() {
    const d = new Date(currentDate);
    if (viewMode === 'jour') d.setDate(d.getDate() - 1);
    else if (viewMode === 'semaine') d.setDate(d.getDate() - 7);
    else d.setMonth(d.getMonth() - 1);
    setCurrentDate(d);
  }
  function navigateNext() {
    const d = new Date(currentDate);
    if (viewMode === 'jour') d.setDate(d.getDate() + 1);
    else if (viewMode === 'semaine') d.setDate(d.getDate() + 7);
    else d.setMonth(d.getMonth() + 1);
    setCurrentDate(d);
  }
  function goToday() { setCurrentDate(new Date()); }

  // ── Filtrage par période ───────────────────────────────────────────────────
  const filteredLivraisons = useMemo(() => {
    const d = new Date(currentDate);
    return planning.filter(liv => {
      if (!liv.date_evenement) return false;
      const livDate = new Date(liv.date_evenement);
      if (viewMode === 'jour') {
        return livDate.toDateString() === d.toDateString();
      } else if (viewMode === 'semaine') {
        const startWeek = new Date(d);
        startWeek.setDate(d.getDate() - ((d.getDay() + 6) % 7));
        startWeek.setHours(0, 0, 0, 0);
        const endWeek = new Date(startWeek);
        endWeek.setDate(startWeek.getDate() + 6);
        endWeek.setHours(23, 59, 59);
        return livDate >= startWeek && livDate <= endWeek;
      } else {
        return livDate.getMonth() === d.getMonth() && livDate.getFullYear() === d.getFullYear();
      }
    });
  }, [planning, currentDate, viewMode]);

  // ── Détection conflits d'horaires ──────────────────────────────────────────
  function detectConflicts(livraisons) {
    const conflicts = new Set();
    for (let i = 0; i < livraisons.length; i++) {
      for (let j = i + 1; j < livraisons.length; j++) {
        const a = livraisons[i];
        const b = livraisons[j];
        if (a.date_evenement === b.date_evenement &&
            a.heure_livraison && b.heure_livraison &&
            a.heure_livraison === b.heure_livraison) {
          conflicts.add(a.id);
          conflicts.add(b.id);
        }
      }
    }
    return conflicts;
  }
  const conflictIds = useMemo(() => detectConflicts(filteredLivraisons), [filteredLivraisons]);

  // ── Titre période ──────────────────────────────────────────────────────────
  const periodeLabel = useMemo(() => {
    const d = currentDate;
    if (viewMode === 'jour') {
      return formatDateWithDay(d);
    } else if (viewMode === 'semaine') {
      const start = new Date(d);
      start.setDate(d.getDate() - ((d.getDay() + 6) % 7));
      const end = new Date(start); end.setDate(start.getDate() + 6);
      return `Semaine du ${formatDateShort(start)} au ${formatDate(end)}`;
    } else {
      return `${MONTH_FR[d.getMonth()]} ${d.getFullYear()}`;
    }
  }, [currentDate, viewMode]);

  // ── Vue mois : grille calendrier ───────────────────────────────────────────
  const calendarGrid = useMemo(() => {
    if (viewMode !== 'mois') return null;
    const d = currentDate;
    const firstDay = new Date(d.getFullYear(), d.getMonth(), 1);
    const lastDay  = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    const startOffset = (firstDay.getDay() + 6) % 7;
    const days = [];
    for (let i = 0; i < startOffset; i++) days.push(null);
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const date = new Date(d.getFullYear(), d.getMonth(), i);
      const dateStr = date.toISOString().slice(0, 10);
      const livsThisDay = planning.filter(l => l.date_evenement === dateStr);
      days.push({ date, dateStr, livraisons: livsThisDay });
    }
    return days;
  }, [currentDate, viewMode, planning]);

  async function sendNotif(quoteId, scenario) {
    setSending(prev => ({ ...prev, [`${quoteId}-${scenario}`]: true }));
    try {
      const res = await fetch(`${BACKEND}/api/admin/livraisons/${quoteId}/notifier`, {
        method: 'POST', headers: H, body: JSON.stringify({ scenario })
      });
      if (!res.ok) throw new Error();
      toast.success(`Notification ${scenario} envoyée !`);
      load();
    } catch { toast.error('Erreur envoi'); }
    setSending(prev => ({ ...prev, [`${quoteId}-${scenario}`]: false }));
  }

  async function runAutoNotifs() {
    setRunningAuto(true);
    try {
      const res = await fetch(`${BACKEND}/api/admin/livraisons/run-auto`, { method: 'POST', headers: H });
      const data = await res.json();
      toast.success(`Auto: J-7: ${data['J-7_envoyes'] || 0} envoyé(s), J-2: ${data['J-2_envoyes'] || 0} envoyé(s)`);
      load();
    } catch { toast.error('Erreur'); }
    setRunningAuto(false);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }} data-testid="admin-livraisons">

      {/* ── Header cockpit ── */}
      <div style={{ marginBottom: 4 }}>
        <p className="adm-eyebrow">Planning</p>
        <div className="flex items-center justify-between flex-wrap gap-3" style={{ marginTop: 4 }}>
          <div>
            <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
              letterSpacing: '-.6px', color: 'var(--admin-text)' }}>Calendrier livraisons</h2>
            <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>
              Agenda, carte interactive, créneaux horaires
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={load} variant="outline" size="sm"
              style={{ borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }}>
              <RefreshCw size={14} className="mr-1" /> Actualiser
            </Button>
            <Button onClick={runAutoNotifs} disabled={runningAuto} size="sm"
              style={{ background: 'var(--admin-teal-dim)', color: 'var(--admin-teal)', border: '1px solid var(--admin-teal-line)' }}>
              <Bell size={14} className="mr-1" /> {runningAuto ? 'Envoi…' : 'Notifs J-7/J-2'}
            </Button>
          </div>
        </div>
      </div>

      {/* ── KPIs ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: "Aujourd'hui",    value: stats.aujourd_hui   || 0, color: 'var(--admin-green)' },
          { label: 'Cette semaine',  value: stats.cette_semaine || 0, color: 'var(--admin-teal)' },
          { label: 'À notifier J-7', value: stats.a_notifier_J7 || 0, color: 'var(--admin-amber)' },
          { label: 'À notifier J-2', value: stats.a_notifier_J2 || 0, color: 'var(--admin-red)' },
        ].map((s, i) => (
          <div key={i} className="p-4 rounded-xl" style={{ background: 'var(--admin-surface)', border: `1px solid ${s.color}20` }}>
            <p className="adm-title"  style={{ color: s.color }}>{s.value}</p>
            <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* ── Barre navigation calendrier + toggle vue ── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {/* Navigation */}
        <div className="flex items-center gap-2">
          <button onClick={navigatePrev}
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid var(--admin-border)', color: 'var(--admin-text)', cursor: 'pointer' }}>
            <ChevronLeft size={14} />
          </button>
          <button onClick={goToday}
            className="px-3 h-8 rounded-lg text-xs font-semibold"
            style={{ background: 'var(--admin-teal-dim)', border: '1px solid rgba(93,188,191,0.25)', color: 'var(--admin-teal)', cursor: 'pointer' }}>
            Aujourd'hui
          </button>
          <button onClick={navigateNext}
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid var(--admin-border)', color: 'var(--admin-text)', cursor: 'pointer' }}>
            <ChevronRight size={14} />
          </button>
          <p className="text-sm font-semibold ml-1" style={{ color: 'var(--admin-text)' }}>{periodeLabel}</p>
        </div>

        <div className="flex items-center gap-2">
          {/* Toggle Jour / Semaine / Mois */}
          <div className="flex rounded-xl overflow-hidden" style={{ border: '1px solid var(--admin-border)' }}>
            {[
              { key: 'jour',    icon: <CalendarDays size={13} />,  label: 'Jour' },
              { key: 'semaine', icon: <CalendarRange size={13} />, label: 'Semaine' },
              { key: 'mois',    icon: <Calendar size={13} />,      label: 'Mois' },
            ].map(v => (
              <button key={v.key} onClick={() => setViewMode(v.key)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold"
                style={{
                  background: viewMode === v.key ? 'var(--admin-teal)' : 'transparent',
                  color:      viewMode === v.key ? '#0B1E20' : 'var(--admin-muted)',
                  border: 'none', cursor: 'pointer',
                }}>
                {v.icon} {v.label}
              </button>
            ))}
          </div>
          {/* Toggle Liste / Carte */}
          <div className="flex rounded-xl overflow-hidden" style={{ border: '1px solid var(--admin-border)' }}>
            {[
              { key: 'liste', icon: <List size={13} />,  label: 'Liste' },
              { key: 'carte', icon: <Map size={13} />,   label: 'Carte' },
            ].map(v => (
              <button key={v.key} onClick={() => setDisplayMode(v.key)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold"
                style={{
                  background: displayMode === v.key ? 'var(--admin-gold)' : 'transparent',
                  color:      displayMode === v.key ? '#0B1E20' : 'var(--admin-muted)',
                  border: 'none', cursor: 'pointer',
                }}>
                {v.icon} {v.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Carte interactive ── */}
      {displayMode === 'carte' && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Navigation size={14} style={{ color: 'var(--admin-teal)' }} />
            <p className="text-sm font-semibold" style={{ color: 'var(--admin-text)' }}>
              Carte de tournée — {filteredLivraisons.length} arrêt(s)
            </p>
            <span className="text-xs px-2 py-0.5 rounded-full"
              style={{ background: 'var(--admin-teal-dim)', color: 'var(--admin-teal)' }}>
              Itinéraire suggéré en bleu
            </span>
          </div>
          <CartelivraIsons livraisons={filteredLivraisons} />
          {/* Légende ordre tournée */}
          {filteredLivraisons.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {filteredLivraisons.map((liv, idx) => (
                <div key={liv.id} className="flex items-center gap-1.5 px-2 py-1 rounded-lg"
                  style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
                  <span className="text-xs font-bold px-1.5 rounded"
                    style={{ background: STATUT_COLORS[liv.statut] || 'var(--admin-teal)', color: 'var(--admin-text)' }}>
                    #{idx + 1}
                  </span>
                  <span className="text-xs" style={{ color: 'var(--admin-text)' }}>{liv.client_nom}</span>
                  {liv.heure_livraison && (
                    <span className="text-xs" style={{ color: 'var(--admin-muted)' }}>{liv.heure_livraison}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Vue Mois : grille calendrier ── */}
      {viewMode === 'mois' && displayMode === 'liste' && calendarGrid && (
        <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid var(--admin-border-soft)' }}>
          {/* En-têtes jours */}
          <div className="grid grid-cols-7" style={{ background: 'var(--admin-surface)', borderBottom: '1px solid var(--admin-border-soft)' }}>
            {DAY_SHORT.map((d, i) => (
              <div key={i} className="text-center text-xs font-bold py-2"
                style={{ color: i >= 5 ? 'var(--admin-teal)' : 'var(--admin-muted)' }}>
                {d}
              </div>
            ))}
          </div>
          {/* Grille */}
          <div className="grid grid-cols-7">
            {calendarGrid.map((cell, i) => {
              if (!cell) return <div key={i} style={{ background: 'var(--admin-bg)', minHeight: 80, borderRight: '1px solid rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.04)' }} />;
              const today = new Date().toDateString() === cell.date.toDateString();
              return (
                <div key={i} className="p-1.5"
                  style={{ minHeight: 80, background: today ? 'rgba(93,188,191,0.04)' : 'var(--admin-bg)', borderRight: '1px solid rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <p className="text-xs font-bold mb-1 text-right"
                    style={{ color: today ? 'var(--admin-teal)' : 'var(--admin-faint)' }}>
                    {cell.date.getDate()}
                  </p>
                  {cell.livraisons.slice(0, 3).map((liv, j) => (
                    <button key={j} onClick={() => setSelectedLivraison(liv)}
                      className="w-full text-left px-1.5 py-0.5 rounded text-xs mb-0.5 truncate"
                      style={{ background: `${STATUT_COLORS[liv.statut] || 'var(--admin-teal)'}20`, color: STATUT_COLORS[liv.statut] || 'var(--admin-teal)', border: 'none', cursor: 'pointer' }}>
                      {liv.heure_livraison && <span className="font-bold mr-1">{liv.heure_livraison}</span>}
                      {liv.client_nom}
                    </button>
                  ))}
                  {cell.livraisons.length > 3 && (
                    <p className="text-xs" style={{ color: 'var(--admin-faint)' }}>+{cell.livraisons.length - 3}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Vue Liste (Jour / Semaine) ── */}
      {displayMode === 'liste' && viewMode !== 'mois' && (
        loading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => <Skeleton key={i} className="h-20 rounded-xl" style={{ background: 'var(--admin-surface)' }} />)}
          </div>
        ) : filteredLivraisons.length === 0 ? (
          <div className="text-center py-16 rounded-2xl" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
            <Truck size={40} className="mx-auto mb-3 opacity-20" style={{ color: 'var(--admin-teal)' }} />
            <p style={{ color: 'var(--admin-faint)' }}>Aucune livraison sur cette période</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredLivraisons.map(liv => {
              const s = STATUT_COLORS[liv.statut] || 'var(--admin-teal)';
              const hasConflict = conflictIds.has(liv.id);
              const diffDays = liv.date_evenement
                ? Math.floor((new Date(liv.date_evenement) - new Date()) / (1000 * 60 * 60 * 24))
                : null;

              return (
                <div key={liv.id} className="rounded-2xl overflow-hidden"
                  style={{ background: 'var(--admin-surface)', border: `1px solid ${hasConflict ? 'rgba(239,68,68,0.4)' : 'var(--admin-border-soft)'}` }}>

                  {/* Alerte conflit */}
                  {hasConflict && (
                    <div className="px-4 py-2 flex items-center gap-2"
                      style={{ background: 'var(--admin-red-dim)', borderBottom: '1px solid rgba(239,68,68,0.2)' }}>
                      <AlertTriangle size={13} style={{ color: 'var(--admin-red)' }} />
                      <p className="text-xs font-semibold" style={{ color: 'var(--admin-red)' }}>
                        ⚠️ Conflit d'horaire — même créneau qu'une autre livraison
                      </p>
                    </div>
                  )}

                  <div className="p-4">
                    <div className="flex items-start gap-4">
                      {/* Badge J-X */}
                      <div className="flex-shrink-0 w-14 h-14 rounded-xl flex flex-col items-center justify-center"
                        style={{ background: diffDays !== null && diffDays <= 2 ? 'rgba(229,62,62,0.15)' : 'rgba(255,255,255,0.04)', border: `1px solid ${s}30` }}>
                        <p className="text-xs font-bold" style={{ color: s }}>
                          {diffDays !== null ? (diffDays === 0 ? 'Auj.' : diffDays < 0 ? 'Passé' : `J-${diffDays}`) : '—'}
                        </p>
                        <p className="text-xs font-semibold" style={{ color: 'var(--admin-muted)' }}>
                          {liv.date_evenement ? formatDateShort(liv.date_evenement) : ''}
                        </p>
                      </div>

                      {/* Infos principales */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <p className="font-bold text-sm" style={{ color: 'var(--admin-text)' }}>{liv.client_nom}</p>
                          <span className="text-xs px-2 py-0.5 rounded-full"
                            style={{ background: `${s}20`, color: s }}>
                            {STATUT_LABELS[liv.statut] || liv.statut}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 mb-1">
                          <MapPin size={11} style={{ color: 'var(--admin-teal)', flexShrink: 0 }} />
                          <p className="text-xs truncate" style={{ color: 'var(--admin-muted)' }}>
                            {liv.adresse_livraison || liv.session?.adresse || '—'}
                          </p>
                        </div>
                        <p className="text-xs" style={{ color: 'var(--admin-faint)' }}>
                          {liv.numero} · {liv.items?.length || 0} article(s) · {liv.total}€
                        </p>

                        {/* Créneaux horaires */}
                        <div className="flex items-center gap-3 mt-2 flex-wrap">
                          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg"
                            style={{ background: liv.heure_livraison ? 'rgba(93,188,191,0.1)' : 'rgba(255,255,255,0.04)', border: `1px solid ${liv.heure_livraison ? 'rgba(93,188,191,0.3)' : 'var(--admin-border-soft)'}` }}>
                            <Truck size={10} style={{ color: 'var(--admin-teal)' }} />
                            <span className="text-xs font-semibold" style={{ color: liv.heure_livraison ? 'var(--admin-teal)' : 'var(--admin-faint)' }}>
                              {liv.heure_livraison || 'Livraison ?'}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg"
                            style={{ background: 'var(--admin-gold-dim)', border: '1px solid rgba(212,175,55,0.2)' }}>
                            <RotateCcw size={10} style={{ color: 'var(--admin-gold)' }} />
                            <span className="text-xs font-semibold" style={{ color: 'var(--admin-gold)' }}>
                              Retour {liv.heure_retour || '10:00'}
                            </span>
                          </div>
                          {liv.client_telephone && (
                            <a href={`tel:${liv.client_telephone}`}
                              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg"
                              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--admin-border-soft)', color: 'var(--admin-muted)', textDecoration: 'none' }}>
                              <Phone size={10} />
                              <span className="text-xs">{liv.client_telephone}</span>
                            </a>
                          )}
                        </div>
                      </div>

                      {/* Bouton édition + lien mission */}
                      <div className="flex gap-1 flex-shrink-0">
                        <a href={`/mission?quote=${liv.id || liv.quote_id || ''}`} target="_blank" rel="noreferrer"
                          className="w-9 h-9 rounded-xl flex items-center justify-center"
                          title="Ouvrir la fiche de mission terrain"
                          style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', color: 'var(--admin-blue)', textDecoration: 'none' }}>
                          <ExternalLink size={13} />
                        </a>
                        <button onClick={() => setSelectedLivraison(liv)}
                          className="w-9 h-9 rounded-xl flex items-center justify-center"
                          style={{ background: 'var(--admin-teal-dim)', border: '1px solid var(--admin-teal-line)', cursor: 'pointer', color: 'var(--admin-teal)' }}>
                          <Edit3 size={13} />
                        </button>
                      </div>
                    </div>

                    {/* Actions notifications */}
                    <div className="flex gap-2 mt-3 pt-3 flex-wrap"
                      style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                      {[
                        { key: 'J-7',    label: 'J-7',    color: 'var(--admin-blue)' },
                        { key: 'J-2',    label: 'J-2',    color: 'var(--admin-amber)' },
                        { key: 'depart', label: '🚚 Départ', color: 'var(--admin-green)' },
                      ].map(n => {
                        const notifKey = `${liv.id}-${n.key}`;
                        const notifSent = liv[`notif_${n.key.replace('-','')}_sent`];
                        return (
                          <button key={n.key}
                            onClick={() => sendNotif(liv.id, n.key)}
                            disabled={!!sending[notifKey]}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold"
                            style={{
                              background: notifSent ? `${n.color}10` : `${n.color}15`,
                              color: notifSent ? `${n.color}80` : n.color,
                              border: `1px solid ${n.color}30`,
                              cursor: sending[notifKey] ? 'wait' : 'pointer',
                              opacity: sending[notifKey] ? 0.6 : 1,
                            }}>
                            {notifSent ? <Check size={10} /> : <Send size={10} />}
                            {sending[notifKey] ? 'Envoi…' : n.label}
                            {notifSent && ' ✓'}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* ── Modal détail / créneaux ── */}
      {selectedLivraison && (
        <LivraisonDetailModal
          livraison={selectedLivraison}
          onClose={() => setSelectedLivraison(null)}
          onSave={load}
        />
      )}
    </div>
  );
}
